import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Vault.css';
import { createFullBackup, AVISO_COPIA_BD } from './services/BackupService';
import { connectGoogleDrive, handleOAuthCallback, isConnected as isDriveConnected, clearTokens as disconnectDrive } from './services/GoogleDriveService';
import { generateContextPrompt } from './services/ContextGenerator';
import speechService from './services/SpeechService';

// API Credentials Data
const API_CREDENTIALS = [
    {
        id: 'supabase',
        name: 'Supabase',
        logo: 'https://supabase.com/favicon/favicon-32x32.png',
        dashboardUrl: 'https://supabase.com/dashboard/project/zapjbtgnmxkhpfykxmnh',
        color: '#3ECF8E',
        keys: [
            { label: 'Project URL', value: 'https://zapjbtgnmxkhpfykxmnh.supabase.co' },
            { label: 'Anon Key', value: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InphcGpidGdubXhraHBmeWt4bW5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY4MDY3NjcsImV4cCI6MjA4MjM4Mjc2N30.boe3ZdBH0Wo_Vmf9Nmhhjh5SnGXq8IG8rRuRlzby1Zg' },
            { label: 'Service Role Key', value: '⚠️ Configurar en Supabase Secrets', sensitive: true },
        ]
    },
    {
        id: 'mapbox',
        name: 'Mapbox',
        logo: 'https://www.mapbox.com/favicon.ico',
        dashboardUrl: 'https://account.mapbox.com/',
        color: '#4264FB',
        keys: [
            { label: 'Access Token', value: 'pk.eyJ1IjoiZml4YXJyIiwiYSI6ImNta3BvdmxiczBlcWQzZnM2cWNobzBodXkifQ.MsyB8tBiEqmq4mflpcttRQ' },
        ]
    },
    {
        id: 'meta',
        name: 'Meta WhatsApp',
        logo: 'https://static.whatsapp.net/rsrc.php/v3/yP/r/rYZqPCBaG70.png',
        dashboardUrl: 'https://developers.facebook.com/apps/',
        color: '#25D366',
        keys: [
            { label: 'WhatsApp Token', value: '⚠️ En Supabase Secrets: META_WHATSAPP_TOKEN', sensitive: true },
            { label: 'Phone Number ID', value: '⚠️ En Supabase Secrets: META_PHONE_NUMBER_ID', sensitive: true },
            { label: 'Verify Token', value: '⚠️ En Supabase Secrets: META_VERIFY_TOKEN', sensitive: true },
        ]
    },
    {
        id: 'resend',
        name: 'Resend',
        logo: 'https://resend.com/favicon-32x32.png',
        dashboardUrl: 'https://resend.com/api-keys',
        color: '#000000',
        keys: [
            { label: 'API Key', value: '⚠️ En Supabase Secrets: RESEND_API_KEY', sensitive: true },
            { label: 'From Email', value: 'noreply@fixarr.es' },
        ]
    },
    {
        id: 'vercel',
        name: 'Vercel',
        logo: 'https://vercel.com/favicon.ico',
        dashboardUrl: 'https://vercel.com/redachaudri-sources-projects',
        color: '#000000',
        keys: [
            { label: 'Panel Admin', value: 'https://panel.fixarr.es' },
            { label: 'Panel Técnico', value: 'https://tech.fixarr.es' },
            { label: 'Roadmap', value: 'https://roadmap.fixarr.es' },
        ]
    }
];

const VaultDashboard = () => {
    const navigate = useNavigate();
    const [copiedKey, setCopiedKey] = useState(null);
    const [backupStatus, setBackupStatus] = useState('idle');
    const [lastBackup, setLastBackup] = useState(null);

    // Context Prompt state
    const [generatedPrompt, setGeneratedPrompt] = useState('');
    const [promptCopied, setPromptCopied] = useState(false);

    // Ideas state
    const [ideas, setIdeas] = useState([]);
    const [isRecording, setIsRecording] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [speechSupported, setSpeechSupported] = useState(true);

    // Google Drive state
    const [driveConnected, setDriveConnected] = useState(false);

    // Load data. 30/10/2026 (auditoría): la puerta ya no es una marca en localStorage; la pone VaultGuard
    // (sesión del roadmap y cuenta del dueño) antes de llegar aquí.
    useEffect(() => {
        // Load last backup info
        const savedBackup = localStorage.getItem('vault-last-backup');
        if (savedBackup) {
            setLastBackup(JSON.parse(savedBackup));
        }

        // Load saved ideas
        const savedIdeas = localStorage.getItem('vault-ideas');
        if (savedIdeas) {
            setIdeas(JSON.parse(savedIdeas));
        }

        // Check speech support
        setSpeechSupported(speechService.isSupported());

        // Check Google Drive connection (async)
        isDriveConnected().then(connected => setDriveConnected(connected));

        // Handle OAuth callback (code in URL)
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        if (code) {
            handleOAuthCallback(code).then((result) => {
                if (result.success) {
                    setDriveConnected(true);
                    // Clean URL
                    window.history.replaceState({}, '', window.location.pathname);
                } else {
                    console.error('Google Drive OAuth failed:', result.error);
                }
            });
        }
    }, [navigate]);

    const handleLogout = () => {
        // Restos de la puerta vieja (marca en localStorage), por si quedaban en este navegador.
        localStorage.removeItem('vault-auth');
        localStorage.removeItem('vault-auth-time');
        navigate('/project/fixarr');
    };

    const copyToClipboard = async (value, keyId) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopiedKey(keyId);
            setTimeout(() => setCopiedKey(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleCreateBackup = async () => {
        setBackupStatus('creating');

        try {
            const result = await createFullBackup((progress) => {
                // Could update a progress bar here in the future
                console.log(`Backup progress: ${progress}%`);
            });

            // 29/10/2026 — la copia ya es solo del código (las BD las copia el
            // workflow nocturno): se guarda la fecha y que fue «solo código».
            const backupInfo = {
                date: result.date,
                codeOnly: true,
            };

            localStorage.setItem('vault-last-backup', JSON.stringify(backupInfo));
            setLastBackup(backupInfo);
            setBackupStatus('done');

            setTimeout(() => setBackupStatus('idle'), 5000);
        } catch (error) {
            console.error('Error creating backup:', error);
            setBackupStatus('error');
            alert('Error descargando el código. Revisa la consola para más detalles.');
            setTimeout(() => setBackupStatus('idle'), 3000);
        }
    };

    const formatDate = (isoDate) => {
        if (!isoDate) return 'Nunca';
        const date = new Date(isoDate);
        return date.toLocaleString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // === Context Prompt Functions ===
    const handleGeneratePrompt = () => {
        const checklistState = JSON.parse(localStorage.getItem('fixarr-roadmap-checklist-v1') || '{}');
        const prompt = generateContextPrompt(checklistState);
        setGeneratedPrompt(prompt);
    };

    const handleCopyPrompt = async () => {
        try {
            await navigator.clipboard.writeText(generatedPrompt);
            setPromptCopied(true);
            setTimeout(() => setPromptCopied(false), 3000);
        } catch (err) {
            console.error('Failed to copy prompt:', err);
        }
    };

    // === Voice Ideas Functions ===
    const handleStartRecording = () => {
        setTranscript('');
        setIsRecording(true);

        speechService.onResult = (result) => {
            setTranscript(result.full);
        };

        speechService.onEnd = (finalText) => {
            setIsRecording(false);
            setTranscript(finalText);
        };

        speechService.onError = (error) => {
            setIsRecording(false);
            alert(`Error de reconocimiento: ${error}`);
        };

        speechService.start();
    };

    const handleStopRecording = () => {
        const finalText = speechService.stop();
        setTranscript(finalText);
        setIsRecording(false);
    };

    const handleSaveIdea = () => {
        if (!transcript.trim()) return;

        const title = speechService.generateTitle(transcript);
        const newIdea = {
            id: Date.now(),
            title: title,
            content: transcript,
            date: new Date().toISOString()
        };

        const updatedIdeas = [newIdea, ...ideas];
        setIdeas(updatedIdeas);
        localStorage.setItem('vault-ideas', JSON.stringify(updatedIdeas));
        setTranscript('');
    };

    const handleDeleteIdea = (id) => {
        const updatedIdeas = ideas.filter(idea => idea.id !== id);
        setIdeas(updatedIdeas);
        localStorage.setItem('vault-ideas', JSON.stringify(updatedIdeas));
    };

    const handleCopyIdea = async (content) => {
        try {
            await navigator.clipboard.writeText(content);
            alert('💡 Idea copiada al portapapeles');
        } catch (err) {
            console.error('Failed to copy idea:', err);
        }
    };

    return (
        <div className="vault-container">
            <div className="vault-bg">
                <div className="vault-grid-overlay"></div>
                <div className="vault-orb orb-1"></div>
                <div className="vault-orb orb-2"></div>
                <div className="vault-orb orb-3"></div>
            </div>

            {/* Header */}
            <div className="vault-header">
                <div className="vault-header-left">
                    <span className="vault-icon">🔐</span>
                    <div>
                        <h1>FIXARR VAULT</h1>
                        <p>Centro de Control de APIs y Backups</p>
                    </div>
                </div>
                <div className="vault-header-right">
                    <Link to="/" className="vault-btn vault-btn-secondary">
                        ← Roadmap
                    </Link>
                    <button onClick={handleLogout} className="vault-btn vault-btn-danger">
                        🚪 Salir del Vault
                    </button>
                </div>
            </div>

            <div className="vault-content">
                {/* Context Prompt Generator Section */}
                <div className="vault-section">
                    <div className="vault-section-header">
                        <h2>📋 Generador de Prompt de Contexto</h2>
                        <span className="vault-badge">Nuevo Chat</span>
                    </div>

                    <div className="vault-prompt-panel">
                        <p className="vault-prompt-description">
                            Genera un resumen completo del proyecto para continuar el trabajo en un chat nuevo cuando el actual se sature.
                        </p>

                        <div className="vault-prompt-actions">
                            <button
                                className="vault-btn vault-btn-primary"
                                onClick={handleGeneratePrompt}
                            >
                                📝 Generar Prompt de Contexto
                            </button>

                            {generatedPrompt && (
                                <button
                                    className={`vault-btn ${promptCopied ? 'vault-btn-success' : 'vault-btn-secondary'}`}
                                    onClick={handleCopyPrompt}
                                >
                                    {promptCopied ? '✅ Copiado!' : '📋 Copiar Prompt'}
                                </button>
                            )}
                        </div>

                        {generatedPrompt && (
                            <div className="vault-prompt-preview">
                                <div className="vault-prompt-preview-header">
                                    <span>Vista previa del prompt</span>
                                    <span className="vault-prompt-chars">{generatedPrompt.length} caracteres</span>
                                </div>
                                <pre className="vault-prompt-text">{generatedPrompt}</pre>
                            </div>
                        )}
                    </div>
                </div>

                {/* Ideas Section */}
                <div className="vault-section">
                    <div className="vault-section-header">
                        <h2>💡 Banco de Ideas</h2>
                        <span className="vault-badge">{ideas.length} guardadas</span>
                    </div>

                    <div className="vault-ideas-panel">
                        {!speechSupported ? (
                            <div className="vault-ideas-unsupported">
                                ⚠️ Tu navegador no soporta reconocimiento de voz. Usa Chrome o Edge.
                            </div>
                        ) : (
                            <>
                                <div className="vault-ideas-recorder">
                                    <div className="vault-ideas-controls">
                                        {!isRecording ? (
                                            <button
                                                className="vault-btn vault-btn-record"
                                                onClick={handleStartRecording}
                                            >
                                                🎤 Dictar Nueva Idea
                                            </button>
                                        ) : (
                                            <button
                                                className="vault-btn vault-btn-stop"
                                                onClick={handleStopRecording}
                                            >
                                                ⏹️ Parar Grabación
                                            </button>
                                        )}

                                        {transcript && !isRecording && (
                                            <button
                                                className="vault-btn vault-btn-primary"
                                                onClick={handleSaveIdea}
                                            >
                                                💾 Guardar Idea
                                            </button>
                                        )}
                                    </div>

                                    {(isRecording || transcript) && (
                                        <div className={`vault-ideas-transcript ${isRecording ? 'recording' : ''}`}>
                                            <div className="vault-transcript-header">
                                                {isRecording && <span className="recording-dot"></span>}
                                                <span>{isRecording ? 'Escuchando...' : 'Transcripción:'}</span>
                                            </div>
                                            <div className="vault-transcript-text">
                                                {transcript || 'Habla ahora...'}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {ideas.length > 0 && (
                                    <div className="vault-ideas-list">
                                        <h3>Ideas Guardadas</h3>
                                        {ideas.map(idea => (
                                            <div key={idea.id} className="vault-idea-card">
                                                <div className="vault-idea-header">
                                                    <span className="vault-idea-title">📌 {idea.title}</span>
                                                    <span className="vault-idea-date">{formatDate(idea.date)}</span>
                                                </div>
                                                <div className="vault-idea-content">{idea.content}</div>
                                                <div className="vault-idea-actions">
                                                    <button
                                                        className="vault-idea-btn"
                                                        onClick={() => handleCopyIdea(idea.content)}
                                                    >
                                                        📋 Copiar
                                                    </button>
                                                    <button
                                                        className="vault-idea-btn vault-idea-btn-danger"
                                                        onClick={() => handleDeleteIdea(idea.id)}
                                                    >
                                                        🗑️ Eliminar
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* APIs Section */}
                <div className="vault-section">
                    <div className="vault-section-header">
                        <h2>⚡ APIs & Credenciales</h2>
                        <span className="vault-badge">{API_CREDENTIALS.length} servicios</span>
                    </div>

                    <div className="vault-apis-grid">
                        {API_CREDENTIALS.map((api) => (
                            <div key={api.id} className="vault-api-card" style={{ '--api-color': api.color }}>
                                <div className="vault-api-header">
                                    <img src={api.logo} alt={api.name} className="vault-api-logo" />
                                    <div className="vault-api-info">
                                        <h3>{api.name}</h3>
                                        <a href={api.dashboardUrl} target="_blank" rel="noopener noreferrer" className="vault-api-link">
                                            🔗 Dashboard
                                        </a>
                                    </div>
                                </div>

                                <div className="vault-api-keys">
                                    {api.keys.map((key, idx) => (
                                        <div key={idx} className={`vault-key-row ${key.sensitive ? 'sensitive' : ''}`}>
                                            <span className="vault-key-label">{key.label}</span>
                                            <div className="vault-key-value-container">
                                                <span className="vault-key-value">
                                                    {key.value.length > 40 ? `${key.value.substring(0, 40)}...` : key.value}
                                                </span>
                                                {!key.sensitive && (
                                                    <button
                                                        className={`vault-copy-btn ${copiedKey === `${api.id}-${idx}` ? 'copied' : ''}`}
                                                        onClick={() => copyToClipboard(key.value, `${api.id}-${idx}`)}
                                                    >
                                                        {copiedKey === `${api.id}-${idx}` ? '✅' : '📋'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Backups Section */}
                <div className="vault-section">
                    <div className="vault-section-header">
                        <h2>📦 Copia del código</h2>
                        <span className="vault-badge">GitHub</span>
                    </div>

                    <div className="vault-backup-panel">
                        <div className="vault-backup-info">
                            <div className="vault-backup-stat">
                                <span className="vault-backup-label">Último Backup</span>
                                <span className="vault-backup-value">{formatDate(lastBackup?.date)}</span>
                            </div>
                            {lastBackup?.codeOnly && (
                                <div className="vault-backup-stat">
                                    <span className="vault-backup-label">Contenido</span>
                                    <span className="vault-backup-value">Solo código (GitHub)</span>
                                </div>
                            )}
                            {/* Copias antiguas (antes del 29/10/2026, con BD): se siguen enseñando sus datos. */}
                            {lastBackup && !lastBackup.codeOnly && (
                                <>
                                    <div className="vault-backup-stat">
                                        <span className="vault-backup-label">Tamaño</span>
                                        <span className="vault-backup-value">{lastBackup.size}</span>
                                    </div>
                                    <div className="vault-backup-stat">
                                        <span className="vault-backup-label">Total Tablas</span>
                                        <span className="vault-backup-value">{lastBackup.tables || 0} tablas</span>
                                    </div>
                                    {lastBackup.summary && (
                                        <>
                                            <div className="vault-backup-stat">
                                                <span className="vault-backup-label">📊 fixarr.es</span>
                                                <span className="vault-backup-value">{lastBackup.summary.fixarrTables || lastBackup.summary.totalTables || 0} tablas</span>
                                            </div>
                                            <div className="vault-backup-stat">
                                                <span className="vault-backup-label">🏢 fixarr-central</span>
                                                <span className="vault-backup-value">{lastBackup.summary.centralTables || 0} tablas</span>
                                            </div>
                                            <div className="vault-backup-stat">
                                                <span className="vault-backup-label">🏗️ fixarr-tenants</span>
                                                <span className="vault-backup-value">{lastBackup.summary.tenantsTables || 0} tablas</span>
                                            </div>
                                            {lastBackup.summary.totalFunctions > 0 && (
                                                <div className="vault-backup-stat">
                                                    <span className="vault-backup-label">⚡ Funciones</span>
                                                    <span className="vault-backup-value">{lastBackup.summary.totalFunctions}</span>
                                                </div>
                                            )}
                                            {lastBackup.summary.totalTriggers > 0 && (
                                                <div className="vault-backup-stat">
                                                    <span className="vault-backup-label">🔄 Triggers</span>
                                                    <span className="vault-backup-value">{lastBackup.summary.totalTriggers}</span>
                                                </div>
                                            )}
                                            {lastBackup.summary.totalPolicies > 0 && (
                                                <div className="vault-backup-stat">
                                                    <span className="vault-backup-label">🛡️ Policies RLS</span>
                                                    <span className="vault-backup-value">{lastBackup.summary.totalPolicies}</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {lastBackup.branchName && (
                                        <div className="vault-backup-stat">
                                            <span className="vault-backup-label">🌿 Branch</span>
                                            <span className="vault-backup-value">{lastBackup.branchName}</span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="vault-backup-actions">
                            <button
                                className={`vault-btn vault-btn-primary ${backupStatus === 'creating' ? 'loading' : ''}`}
                                onClick={handleCreateBackup}
                                disabled={backupStatus === 'creating'}
                            >
                                {backupStatus === 'creating' ? (
                                    <>⏳ Descargando el código...</>
                                ) : backupStatus === 'done' ? (
                                    <>✅ Código descargado</>
                                ) : (
                                    <>💾 Descargar el código (GitHub)</>
                                )}
                            </button>

                        </div>

                        {/* Google Drive */}
                        <div className="vault-backup-actions" style={{ marginTop: '12px' }}>
                            {driveConnected ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                                    <span style={{ color: '#3ECF8E', fontSize: '14px' }}>✅ Google Drive conectado</span>
                                    <button
                                        className="vault-btn vault-btn-secondary"
                                        onClick={async () => { await disconnectDrive(); setDriveConnected(false); }}
                                        style={{ marginLeft: 'auto', fontSize: '12px', padding: '4px 10px' }}
                                    >
                                        Desconectar
                                    </button>
                                </div>
                            ) : (
                                <button
                                    className="vault-btn vault-btn-secondary"
                                    onClick={connectGoogleDrive}
                                    style={{ width: '100%' }}
                                >
                                    📁 Conectar Google Drive
                                </button>
                            )}
                        </div>

                        {/* Drive upload result */}
                        {lastBackup?.drive && (
                            <div style={{ marginTop: '8px', padding: '8px 12px', borderRadius: '8px', background: lastBackup.drive.success ? 'rgba(62,207,142,0.1)' : 'rgba(255,107,107,0.1)', fontSize: '13px' }}>
                                {lastBackup.drive.success ? (
                                    <span>☁️ Subido a Drive: <a href={lastBackup.drive.webViewLink} target="_blank" rel="noopener noreferrer" style={{ color: '#4285f4' }}>{lastBackup.drive.fileName}</a></span>
                                ) : (
                                    <span>❌ Drive: {lastBackup.drive.error}</span>
                                )}
                            </div>
                        )}

                        <div className="vault-backup-note">
                            <span>💡</span>
                            <span>{AVISO_COPIA_BD}</span>
                        </div>
                    </div>
                </div>

                {/* Quick Links */}
                <div className="vault-section">
                    <div className="vault-section-header">
                        <h2>🔗 Accesos Rápidos</h2>
                    </div>

                    <div className="vault-quick-links">
                        <a href="https://supabase.com/dashboard/project/zapjbtgnmxkhpfykxmnh/settings/api" target="_blank" rel="noopener noreferrer" className="vault-quick-link">
                            <span>🔑</span>
                            <span>Supabase API Keys</span>
                        </a>
                        <a href="https://supabase.com/dashboard/project/zapjbtgnmxkhpfykxmnh/settings/vault" target="_blank" rel="noopener noreferrer" className="vault-quick-link">
                            <span>🔐</span>
                            <span>Supabase Secrets</span>
                        </a>
                        <a href="https://supabase.com/dashboard/project/zapjbtgnmxkhpfykxmnh/storage" target="_blank" rel="noopener noreferrer" className="vault-quick-link">
                            <span>📦</span>
                            <span>Supabase Storage</span>
                        </a>
                        <a href="https://vercel.com/redachaudri-sources-projects/fixarr-admin/settings/environment-variables" target="_blank" rel="noopener noreferrer" className="vault-quick-link">
                            <span>▲</span>
                            <span>Vercel Env Vars</span>
                        </a>
                        <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer" className="vault-quick-link">
                            <span>📱</span>
                            <span>Meta Developer</span>
                        </a>
                        <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="vault-quick-link">
                            <span>📧</span>
                            <span>Resend API Keys</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="vault-footer">
                <span>🔒 Sesión activa</span>
                <span>•</span>
                <span>FIXARR Vault v2.0</span>
            </div>
        </div>
    );
};

export default VaultDashboard;
